/**
 * Shared manufacturing-date parsing for Inward.
 * Ambiguous day/month values (both <= 12) are always interpreted as DD/MM/YYYY.
 * Never uses locale-dependent Date("10/07/2026") slash/dash/dot guessing.
 */

export const MFG_DATE_BUSINESS_TZ = "Asia/Kolkata";

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** @returns {string|null} YYYY-MM-DD if valid calendar date */
export function toYmd(year, month, day) {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (y < 1000 || y > 9999 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

export function isCanonicalYmd(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

export function dateObjectToYmd(value, timeZone = MFG_DATE_BUSINESS_TZ) {
  if (!(value instanceof Date) || !Number.isFinite(value.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(value);
}

export function todayYmdBusinessTz(now = new Date(), timeZone = MFG_DATE_BUSINESS_TZ) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now instanceof Date ? now : new Date());
}

export function addDaysToYmd(ymd, days) {
  if (!isCanonicalYmd(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + Number(days || 0));
  return toYmd(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
}

export function isMfgYmdNotFuture(ymd, todayYmd = todayYmdBusinessTz()) {
  if (!ymd) return true;
  if (!isCanonicalYmd(ymd)) return false;
  return ymd <= todayYmd;
}

/**
 * Parse manufacturing date to YYYY-MM-DD.
 * Ambiguous inputs default to DD/MM/YYYY (day before month).
 */
export function parseManufacturingDateToYmd(input) {
  if (input == null || String(input).trim() === "") return null;
  if (input instanceof Date) return dateObjectToYmd(input);

  const raw = String(input).trim();
  const noTime = raw.includes("T") ? raw.split("T")[0].trim() : raw;

  if (/^\d{4}-\d{2}-\d{2}$/.test(noTime)) {
    const [y, m, d] = noTime.split("-").map(Number);
    const asYmd = toYmd(y, m, d);
    if (asYmd) return asYmd;
    return toYmd(y, d, m);
  }

  const match = noTime.match(/^(\d{1,4})([\/.\-])(\d{1,2})\2(\d{1,4})$/);
  if (!match) return null;

  const a = Number(match[1]);
  const b = Number(match[3]);
  const c = Number(match[4]);

  if (String(match[1]).length === 4) {
    const asYmd = toYmd(a, b, c);
    const asYdm = toYmd(a, c, b);
    if (asYmd && !asYdm) return asYmd;
    if (!asYmd && asYdm) return asYdm;
    if (asYmd && asYdm) return asYmd === asYdm ? asYmd : asYdm;
    return null;
  }

  if (String(match[4]).length === 4) {
    const n1 = a;
    const n2 = b;
    const y = c;
    if (n1 > 12) return toYmd(y, b, a);
    if (n2 > 12) return toYmd(y, a, b);
    return toYmd(y, b, a);
  }

  return null;
}

export function assertCanonicalManufacturingYmd(input) {
  if (input == null || String(input).trim() === "") {
    return { ok: true, ymd: null };
  }
  if (input instanceof Date) {
    const ymd = dateObjectToYmd(input);
    return ymd ? { ok: true, ymd } : { ok: false, error: "Invalid manufacturingDate" };
  }
  const raw = String(input).trim();
  if (isCanonicalYmd(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    const ymd = toYmd(y, m, d);
    return ymd ? { ok: true, ymd } : { ok: false, error: "Invalid manufacturingDate calendar day" };
  }
  const ymd = parseManufacturingDateToYmd(raw);
  if (!ymd) {
    return { ok: false, error: `Invalid manufacturingDate format "${raw}". Use YYYY-MM-DD.` };
  }
  return {
    ok: false,
    error: `manufacturingDate must be YYYY-MM-DD (received "${raw}"; normalized form would be ${ymd}).`,
    ymd,
  };
}
